import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';

import { ScrollTrigger } from './animation/gsap';
import { useContent } from './content/ContentProvider';

import Header from './components/Header';
import SchemaOrg from './components/SchemaOrg';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ErrorScreen from './components/ErrorScreen';

// The home page is what nearly everyone lands on, so it is part of the first
// download. The rest are fetched when someone actually goes there, which keeps
// the gallery's lightbox and the contact form's fields out of the initial load.
import Home from './pages/Home';

const Services = lazy(() => import('./pages/Services'));
const Gallery = lazy(() => import('./pages/Gallery'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyLegal = lazy(() => import('./pages/PrivacyLegal'));
const NotFound = lazy(() => import('./pages/NotFound'));

/**
 * Puts the visitor at the top of each new page, and re-measures the animations.
 *
 * Both halves matter. Following a link in a single-page app leaves the scroll
 * position where it was, so without the first half you arrive halfway down.
 * And ScrollTrigger works out its start and end points by measuring the page,
 * so after the new route has rendered those measurements belong to a page that
 * is no longer on screen. Refreshing takes them again.
 */
function useRouteChange() {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);

		// One frame later, so the new route has actually been laid out.
		const frame = requestAnimationFrame(() => ScrollTrigger.refresh());

		return () => cancelAnimationFrame(frame);
	}, [pathname]);
}

/**
 * Re-measures once the gallery photos have arrived.
 *
 * ScrollTrigger works out where a pinned section starts and ends from the height
 * of the page. Photos are lazy-loaded and have real dimensions, so each one that
 * arrives makes the page taller and leaves those measurements describing a page
 * that no longer exists — the symptom is a pin that releases early, or a section
 * that jumps as you scroll past it.
 */
function useRefreshOnLoad() {
	useEffect(() => {
		const refresh = () => ScrollTrigger.refresh();

		// Covers the case where everything is already cached and load has fired.
		if (document.readyState === 'complete') {
			refresh();
		}

		window.addEventListener('load', refresh);

		return () => window.removeEventListener('load', refresh);
	}, []);
}

export default function App() {
	const { status, error } = useContent();

	useRouteChange();
	useRefreshOnLoad();

	// Nothing can be drawn before the content arrives, since even the header's
	// phone number comes from WordPress.
	if (status === 'loading') {
		return <LoadingScreen />;
	}

	if (status === 'error') {
		return <ErrorScreen error={error} />;
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
			{/* Injects the business's JSON-LD on the pages that describe it. */}
			<SchemaOrg />
			<Header />

			<Box component="main" id="main" sx={{ flex: 1 }}>
				{/* Covers the moment between clicking a link and its chunk arriving.
				    A minimum height stops the footer jumping up the screen and back. */}
				<Suspense fallback={<Box sx={{ minHeight: '60vh' }} />}>
					<Routes>
						<Route path="/" element={<Home />} />
						<Route path="/services" element={<Services />} />
						<Route path="/gallery" element={<Gallery />} />
						<Route path="/about" element={<About />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/privacy-legal" element={<PrivacyLegal />} />
						<Route path="*" element={<NotFound />} />
					</Routes>
				</Suspense>
			</Box>

			<Footer />
		</Box>
	);
}
